import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export default function ImageLightbox({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const lastDist = useRef(null);
  const lastPos = useRef(null);

  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const handlePointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (lastDist.current) setScale((s) => Math.min(5, Math.max(1, s * (d / lastDist.current))));
      lastDist.current = d;
    } else if (pts.length === 1 && scale > 1 && lastPos.current) {
      setOffset((o) => ({ x: o.x + (e.clientX - lastPos.current.x), y: o.y + (e.clientY - lastPos.current.y) }));
      lastPos.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handlePointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    lastDist.current = null;
  };

  const toggleZoom = (e) => {
    e.stopPropagation();
    if (scale > 1) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  };

  const handleBackdrop = (e) => {
    e.stopPropagation();
    if (scale > 1) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    } else {
      onClose();
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div data-testid="image-lightbox" className="pointer-events-auto fixed inset-0 z-[100] bg-black/95" onClick={handleBackdrop}>
      <button
        data-testid="lightbox-close"
        aria-label="Close image"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white"
        onClick={handleClose}
      >
        <X className="h-5 w-5" />
      </button>
      <div
        className="flex h-full w-full touch-none items-center justify-center overflow-hidden p-4"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <img
          src={src}
          alt={alt}
          data-testid="lightbox-image"
          draggable={false}
          onClick={toggleZoom}
          className="max-h-full max-w-full select-none object-contain"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: "center center" }}
        />
      </div>
      <p className="pointer-events-none absolute bottom-4 left-0 right-0 text-center text-xs text-white/70">
        Pinch or tap image to zoom · drag to pan · X to close
      </p>
    </div>,
    document.body
  );
}
